import { FileText, MessageSquareText } from 'lucide-react';
import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { type CitationFocus, DocumentViewerContent } from '@/components/case-partner/DocumentViewerContent';
import {
  type ShowCasePartnerTabOptions,
  SidePanelContext,
  type SidePanelTab,
} from './SidePanelContext';

const CASE_PARTNER_TAB_ID = 'case-partner';
const DOCUMENT_TAB_ID = 'document';

export const SidePanelProvider: React.FC<{
  children: ReactNode;
  casePartnerContent?: ReactNode;
  shouldKeepCasePartnerTab?: boolean;
}> = ({ children, casePartnerContent, shouldKeepCasePartnerTab = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tabs, setTabs] = useState<SidePanelTab[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [lastActiveTab, setLastActiveTab] = useState<string>('');
  const [panelWidth, setPanelWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth * 0.4 : 600
  );
  const [activeDocumentGuid, setActiveDocumentGuid] = useState<string | null>(null);
  const [currentDocumentGuid, setCurrentDocumentGuid] = useState<string | null>(null);
  const casePartnerContentRef = useRef<ReactNode>(casePartnerContent);

  if (casePartnerContent && !casePartnerContentRef.current) {
    casePartnerContentRef.current = casePartnerContent;
  }

  const getCasePartnerTab = useCallback((): SidePanelTab => ({
    id: CASE_PARTNER_TAB_ID,
    label: 'Case Partner',
    icon: MessageSquareText,
    content: casePartnerContentRef.current,
  }), []);

  const withCasePartnerTab = useCallback((baseTabs: SidePanelTab[], force = false) => {
    if (!force && !shouldKeepCasePartnerTab) {
      return baseTabs;
    }

    if (baseTabs.some((tab) => tab.id === CASE_PARTNER_TAB_ID)) {
      return baseTabs;
    }

    return [...baseTabs, getCasePartnerTab()];
  }, [getCasePartnerTab, shouldKeepCasePartnerTab]);

  useEffect(() => {
    setTabs((currentTabs) => {
      const hasCasePartnerTab = currentTabs.some((tab) => tab.id === CASE_PARTNER_TAB_ID);

      if (shouldKeepCasePartnerTab) {
        return hasCasePartnerTab ? currentTabs : [...currentTabs, getCasePartnerTab()];
      }

      if (!hasCasePartnerTab) {
        return currentTabs;
      }

      const filteredTabs = currentTabs.filter((tab) => tab.id !== CASE_PARTNER_TAB_ID);

      if (activeTab === CASE_PARTNER_TAB_ID) {
        if (filteredTabs.length > 0) {
          setActiveTab(filteredTabs[0].id);
        } else {
          setActiveTab('');
          setIsOpen(false);
        }
      }

      return filteredTabs;
    });
  }, [activeTab, getCasePartnerTab, shouldKeepCasePartnerTab]);

  const openPanel = (newTabs: SidePanelTab[], activeTabId?: string) => {
    const nextTabs = withCasePartnerTab(newTabs);
    setTabs(nextTabs);
    const tabToActivate = activeTabId ||
      (lastActiveTab && nextTabs.some((tab) => tab.id === lastActiveTab) ? lastActiveTab : nextTabs[0]?.id) ||
      '';
    setActiveTab(tabToActivate);
    setIsOpen(true);
  };

  const openDocumentPanel = ({
    documentGuid,
    matterGuid,
    citationNumber = '',
    chunkId,
    segmentIndex,
    cellRange,
    pageNumber,
    activeTab = 'document',
    attachmentGuid,
    focusCitation,
  }: {
    documentGuid: string;
    matterGuid?: string;
    citationNumber?: string;
    chunkId?: number;
    segmentIndex?: number;
    cellRange?: string;
    pageNumber?: number;
    activeTab?: 'overview' | 'document';
    attachmentGuid?: string;
    focusCitation?: CitationFocus;
  }) => {
    const initialTab = activeTab === 'overview' ? 'document' : activeTab;
    const documentTab: SidePanelTab = {
      id: DOCUMENT_TAB_ID,
      label: 'Document',
      icon: FileText,
      content: (
        <DocumentViewerContent
          // Key by document identity only (not the citation target). Switching between
          // citations of the same document then updates props in place — both PDFViewer
          // and ExcelViewer react to chunk/page/locator prop changes — instead of
          // remounting and fully reloading the viewer. Remounting a spreadsheet also
          // briefly overlaps two Univer instances, which can break the new instance's
          // scroll controller, so avoiding it makes citation navigation reliable too.
          key={`${documentGuid}-${attachmentGuid || ''}-${focusCitation?.canonicalId || ''}`}
          documentGuid={documentGuid}
          matterGuid={matterGuid}
          chunkId={chunkId}
          segmentIndex={segmentIndex}
          cellRange={cellRange}
          initialPageNumber={pageNumber}
          attachmentGuid={attachmentGuid}
          focusCitation={focusCitation}
        />
      ),
    };

    // chunkId / pageNumber can legitimately be 0, so check for undefined rather than
    // falsiness — otherwise clicking a chunk-0 (or page-0) citation for the already-open
    // document hits this "nothing to navigate to" path and never scrolls to the target.
    if (isOpen && currentDocumentGuid === documentGuid && chunkId === undefined && segmentIndex === undefined && pageNumber === undefined && !citationNumber && !focusCitation) {
      setTabs((currentTabs) => withCasePartnerTab(currentTabs));
      setActiveDocumentGuid(attachmentGuid || documentGuid);
      setActiveTab(initialTab);
      return;
    }

    setTabs((currentTabs) => {
      const documentTabIndex = currentTabs.findIndex((tab) => tab.id === DOCUMENT_TAB_ID);
      const nextTabs = documentTabIndex >= 0
        ? currentTabs.map((tab, index) => index === documentTabIndex ? documentTab : tab)
        : [...currentTabs, documentTab];

      return withCasePartnerTab(nextTabs);
    });
    setActiveTab(initialTab);
    setActiveDocumentGuid(attachmentGuid || documentGuid);
    setCurrentDocumentGuid(documentGuid);
    setIsOpen(true);
  };

  const openCasePartnerPanel = useCallback(() => {
    const newTabs = withCasePartnerTab(tabs, true);
    setTabs(newTabs);
    setActiveTab(CASE_PARTNER_TAB_ID);
    setIsOpen(true);
  }, [tabs, withCasePartnerTab]);

  const showCasePartnerTab = useCallback((options: ShowCasePartnerTabOptions = {}) => {
    const { activate = true, open = false } = options;
    setTabs((currentTabs) => withCasePartnerTab(currentTabs, true));
    if (activate) {
      setActiveTab(CASE_PARTNER_TAB_ID);
    }
    if (open) {
      setIsOpen(true);
    }
  }, [withCasePartnerTab]);

  const removeCasePartnerTab = useCallback(() => {
    setTabs((currentTabs) => {
      if (!currentTabs.some((tab) => tab.id === CASE_PARTNER_TAB_ID)) {
        return currentTabs;
      }

      const filteredTabs = currentTabs.filter((tab) => tab.id !== CASE_PARTNER_TAB_ID);

      if (activeTab === CASE_PARTNER_TAB_ID) {
        if (filteredTabs.length > 0) {
          setActiveTab(filteredTabs[0].id);
        } else {
          setActiveTab('');
          setIsOpen(false);
        }
      }

      return filteredTabs;
    });
  }, [activeTab]);

  const closePanel = () => {
    setLastActiveTab(activeTab);
    setActiveDocumentGuid(null);
    setCurrentDocumentGuid(null);
    setIsOpen(false);
  };

  return (
    <SidePanelContext.Provider
      value={{
        isOpen,
        tabs,
        activeTab,
        panelWidth,
        activeDocumentGuid,
        openPanel,
        openDocumentPanel,
        openCasePartnerPanel,
        showCasePartnerTab,
        removeCasePartnerTab,
        closePanel,
        setActiveTab,
        setTabs,
        setPanelWidth,
        setActiveDocumentGuid,
      }}
    >
      {children}
    </SidePanelContext.Provider>
  );
};
